/* eslint-disable react/sort-comp, react/require-default-props, react/prop-types */
import _ from 'underscore';
import classNames from 'classnames';
import { merge, cloneDeep } from '@bingads-webui-universal/primitive-utilities';
import React from 'react';

import { layoutConfigs } from './layout-configs';
import { LayoutContext } from './layout-context';
import { StylesContainer } from './styles-container';
import { names } from './components';
import { useContainerStyles } from './styles';

import { ILayoutConfig } from './types';

export interface ILayoutContainerProps {
  classes?: {
    container?: string;
  };
  persistConfig?: ILayoutConfig;
  pageConfig?: ILayoutConfig;
  controlledByProps?: boolean;
  isGridLayout?: boolean;
  isNavRefreshNew?: boolean;
}

export interface ILayoutContainerState {
  persistConfig: ILayoutConfig;
  pageConfig: ILayoutConfig;
  controlledByProps: boolean;
  isLayoutLocked: boolean;
  ownUpdate?: boolean;
}

class LayoutContainerBase extends React.PureComponent<ILayoutContainerProps, ILayoutContainerState> {
  stylesContainer?: StylesContainer;

  static defaultProps = {
    persistConfig: layoutConfigs.default,
    pageConfig: layoutConfigs.default,
    controlledByProps: false,
    isGridLayout: false,
    isNavRefreshNew: false,
  };

  constructor(props: ILayoutContainerProps) {
    super(props);

    if (!props.isGridLayout) {
      this.stylesContainer = new StylesContainer(props.persistConfig);
    }

    this.state = {
      persistConfig: props.persistConfig,
      pageConfig: props.pageConfig,
      controlledByProps: props.controlledByProps,
      isLayoutLocked: false,
    };
  }

  static getDerivedStateFromProps(nextProps: ILayoutContainerProps, prevState: ILayoutContainerState): Partial<ILayoutContainerState> | null {
    if (!prevState.controlledByProps) {
      return null;
    }
    if (prevState.ownUpdate) {
      // getDerivedStateFromProps is triggered before each render.
      // need to ensure that we dont override changes from its own update
      return { ownUpdate: false };
    } else if (!_.isEqual(nextProps.pageConfig, prevState.pageConfig)) {
      return { pageConfig: nextProps.pageConfig };
    }
    return null;
  }

  componentDidUpdate(prevProps: ILayoutContainerProps, prevState: ILayoutContainerState) {
    if (!_.isEqual(this.state.pageConfig, prevState.pageConfig)) {
      this.updatePage();
    }
  }

  changeLayout = (
    updates: Partial<ILayoutConfig>,
    isPageConfig: boolean,
    isPageConfigPartialUpdate: boolean = false,
    afterChangeLayout: () => void = _.noop,
    lockLayout: boolean = false
  ) => {
    if (_.find(updates, (_item, key) => !_.has(names, key))) {
      throw new Error('Invalid layout configs');
    }

    if (this.state.isLayoutLocked) {
      return;
    }

    const state = this.state as ILayoutContainerState;
    state.isLayoutLocked = lockLayout;

    if (isPageConfig) {
      this.setState((state) => {
        const pageConfig = isPageConfigPartialUpdate
          ? merge(cloneDeep(state.pageConfig), updates)
          : merge(cloneDeep(state.persistConfig), updates);
        return {
          pageConfig: _.isEqual(pageConfig, state.pageConfig) ? state.pageConfig : pageConfig,
          ownUpdate: true,
        };
      }, () => {
        this.updatePage(afterChangeLayout);
      });
    } else {
      this.setState((state) => {
        const persistConfig = merge(cloneDeep(state.persistConfig), updates);
        const pageConfig = merge(cloneDeep(state.pageConfig), updates);
        return {
          persistConfig: _.isEqual(persistConfig, state.persistConfig)
            ? state.persistConfig
            : persistConfig,
          pageConfig: _.isEqual(pageConfig, state.pageConfig) ? state.pageConfig : pageConfig,
          ownUpdate: true,
        };
      }, () => {
        this.updatePage(afterChangeLayout);
      });
    }
  };

  updatePage(afterChangeLayout: () => void = _.noop) {
    if (this.stylesContainer) {
      this.stylesContainer.update(this.state.pageConfig);
    }
    afterChangeLayout();
  }

  render() {
    return (
      <LayoutContext.Provider value={{
        config: this.state.pageConfig,
        styles: this.stylesContainer ? this.stylesContainer.styles : {},
        isGridLayout: this.props.isGridLayout,
        isNavRefreshNew: this.props.isNavRefreshNew,
        change: this.changeLayout,
      }}
      >
        {this.props.isGridLayout ? (
          <div className={classNames('app-root-container', this.props.classes?.container)}>
            {this.props.children}
          </div>
        )
          : this.props.children}
      </LayoutContext.Provider>
    );
  }
}

export function LayoutContainer(props: React.PropsWithChildren<ILayoutContainerProps>) {
  const containerStyles = useContainerStyles();
  return <LayoutContainerBase {...props} classes={{ container: containerStyles.container }} />;
}
